import { Link } from "@tanstack/react-router";
import type { ReactElement } from "react";

import { isElectron } from "../../env";
import { cn, isMacPlatform } from "~/lib/utils";
import { RESPONSIVE_ICON_BUTTON_CLASS } from "~/lib/touchTarget";
import { ForgeWordmark } from "../branding/ForgeWordmark";
import { SidebarTrigger } from "../ui/sidebar";

/**
 * macOS hides its own titlebar in the Electron shell, so the traffic lights
 * float over whatever occupies the top-left. Since the sidebar now runs
 * full-height, that is this header — the inset moved here from the top bar.
 */
export const MACOS_TRAFFIC_LIGHTS_INSET_CLASS = "pl-[88px]";

export function resolveSidebarBrandLinkClassName(): string {
  return "no-drag-region relative z-10 flex min-w-0 cursor-pointer items-center gap-1.5 truncate rounded-md px-1.5 py-1 outline-none focus-visible:ring-2 focus-visible:ring-ring";
}

export function resolveSidebarBrandHeaderClassName(input: {
  readonly reserveMacTrafficLightSpace: boolean;
}): string {
  return cn(
    // Locked to the bar's height so the wordmark and the breadcrumb sit on one
    // optical line across the sidebar seam. This is the whole point of the
    // full-height sidebar; a different height here breaks it.
    //
    // The left padding mirrors the bar's left zone exactly, because the trigger
    // renders in both and must land on the same pixel either way.
    //
    // No bottom rule. The rail already has its own surface, so a line under the
    // wordmark drew a second seam a few pixels from the sidebar's own edge and
    // cut the navigation in half for no grouping reason — t3code's header
    // carries no divider either.
    "drag-region relative flex h-[var(--forge-app-top-bar-height)] shrink-0 items-center gap-1 pr-1.5 md:gap-1.5",
    input.reserveMacTrafficLightSpace ? MACOS_TRAFFIC_LIGHTS_INSET_CLASS : "pl-1 md:pl-2.5",
  );
}

/**
 * Sidebar identity row: the Forge wordmark plus the control that hides it.
 *
 * The trigger lives here rather than in the bar because the thing it collapses
 * is the thing it sits in — and when the sidebar is gone the bar renders its
 * own trigger in the same screen position, so the control never appears to move.
 */
export function SidebarBrandHeader(): ReactElement {
  const shouldReserveMacTrafficLightSpace =
    isElectron && typeof navigator !== "undefined" && isMacPlatform(navigator.platform);

  return (
    <div
      className={resolveSidebarBrandHeaderClassName({
        reserveMacTrafficLightSpace: shouldReserveMacTrafficLightSpace,
      })}
      data-testid="sidebar-brand-header"
    >
      {/* t3code proves the composition: shallow, wide-crop artwork below a
          high-contrast identity layer, with an 80px masked falloff that reads
          as a soft shadow below the 40px header. This scene is original Forge
          artwork, generated for the rail and compressed to a 19 KB WebP. */}
      <div
        aria-hidden="true"
        className="sidebar-brand-backdrop pointer-events-none absolute inset-x-0 top-0 h-[80px] overflow-hidden"
        data-sidebar-brand-artwork="true"
      >
        <img
          alt=""
          className="absolute inset-0 size-full object-cover object-right"
          height={256}
          src="/sidebar/forge-ember-field.webp"
          width={1279}
        />
        <span className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/20" />
      </div>
      {/* Trigger first, wordmark second. The bar renders the same trigger at the
          same left inset once the sidebar is gone, so anchoring it to the
          corner is what keeps the control from appearing to jump on collapse. */}
      <SidebarTrigger
        aria-label="Toggle sidebar"
        className={cn(
          RESPONSIVE_ICON_BUTTON_CLASS,
          "no-drag-region relative z-10 shrink-0 text-primary-foreground/75 hover:bg-sidebar-accent hover:text-primary-foreground focus-visible:ring-ring",
        )}
        data-testid="sidebar-brand-trigger"
      />
      <Link
        aria-label="Go to new thread"
        className={resolveSidebarBrandLinkClassName()}
        data-testid="sidebar-brand-link"
        to="/new"
      >
        <ForgeWordmark tone="on-dark" />
      </Link>
    </div>
  );
}
